import React, { useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import { useDispatch, useSelector } from "react-redux";
import { masterHandler, customerHandler } from "../../../redux/handlers";
import { useGlobalTable } from "../../../hooks/useGlobalTable";
import { AllderTable } from "../../../components/AllderTable";
import { createTableColumns } from "../../../utils/createTableColumns";
import { BusinessModal } from "../modals/BusinessModal";
import { setBusinesses } from "../../../redux/slices/customer.slice";
import dayjs from "dayjs";
import Button from "@mui/material/Button";

export function CustomerBusinessTab() {
  const { businessID } = useParams();
  const dispatch = useDispatch();
  const selectedCustomer = useSelector((state) => state.customer.customer);
  const { prefixNames } = useSelector((state) => state.master);

  const [modalBusiness, setModalBusiness] = useState({
    open: false,
    x: 0,
    y: 0,
    width: 1360,
    height: 860,
    pin: false,
    mode: "normal",
    modalname: "create",
  });

  const fetchBusinessData = useCallback(
    (params) =>
      dispatch(
        customerHandler.getBusinessPagination({
          ...params,
          business_group_id: selectedCustomer?.business_group_id,
          search: params.search || "",
        })
      ),
    [dispatch, selectedCustomer?.business_group_id]
  );

  const {
    data: businessData,
    loading,
    totalRows,
    paginationModel,
    setPaginationModel,
    handleSort,
    debouncedSearch,
    filterParams,
    refreshData,
  } = useGlobalTable({
    fetchData: fetchBusinessData,
    defaultSort: "created_at",
    defaultOrder: "desc",
    defaultPageSize: 20,
  });

  const handleExport = useCallback(() => {
    dispatch(
      customerHandler.exportBusinessToExcel({
        business_group_id: selectedCustomer.business_group_id,
        search: filterParams?.search || "",
      })
    );
  }, [dispatch, selectedCustomer.business_group_id, filterParams?.search]);

  const fetchingMasterData = async () => {
    await Promise.all([
      dispatch(masterHandler.getPrefixNames("NATURAL")),
      dispatch(masterHandler.getProvinces()),
      dispatch(masterHandler.getEntityTypes()),
      dispatch(masterHandler.getRestaurantTypes()),
      dispatch(masterHandler.getShopProductCategories()),
      dispatch(masterHandler.getCurrencies()),
      dispatch(masterHandler.getVatCalculations()),
    ]);
    if (businessID) {
      dispatch(customerHandler.getBusinessGroupById(businessID));
    }
  };

  const handleAddBusiness = async () => {
    fetchingMasterData();    
    dispatch(setBusinesses(null));
    setModalBusiness((prev) => ({
      ...prev,
      open: true,
      modalname: "create",
    }));
  };

  const handleModalClose = useCallback(() => {
    setModalBusiness((prev) => ({ ...prev, open: false }));
    refreshData();
  }, [refreshData]);

  const columns = createTableColumns([
    {
      field: "code",
      headerName: "รหัสกิจการ",
      width: 140,
      sortable: true,
      renderCell: ({ row }) => (
        <Link
          to={`/customer/manage/${row.businessGroup?.user_business_group_id}/business/${row.id}`}
          className="text-[#2DC76D] underline"
        >
          {row.code}
        </Link>
      ),
    },
    {
      field: "company_name",
      headerName: "ชื่อกิจการ",
      width: 280,
      flex: 1,
      minWidth: 280,
      sortable: false,
      renderCell: ({ row }) => (
        <div>
          {row.entity_type_id == 1
            ? row.company_name
            : `${prefixNames?.find((e) => e.id == row.prefix_name_id)?.name} ${
                row.first_name
              } ${row.last_name}`}
        </div>
      ),
    },
    {
      field: "permissionName",
      headerName: "สิทธิ์การใช้งาน",
      width: 180,
      sortable: false,
      renderCell: ({ row }) => <div>{row.permissionName || ""}</div>,
    },
    {
      field: "packageName",
      headerName: "แพ็กเกจ",
      width: 180,
      sortable: false,
      renderCell: ({ row }) => <div>{row?.packageMain?.name || "-"}</div>,
    },
    {
      field: "created_at",
      headerName: "วันที่สร้าง",
      width: 180,
      sortable: true,
      renderCell: ({ row }) => (
        <div>{dayjs(row.created_at).format("DD/MM/YYYY")}</div>
      ),
    },
    {
      field: "countPOS",
      headerName: "จำนวน POS",
      width: 150,
      sortable: false,
      renderCell: ({ row }) => <div>{row.countPOS || 0}</div>,
    },
    {
      field: "is_active",
      headerName: "สถานะ",
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <>
          {row.is_active ? (
            <Chip
              label="เปิดใช้งาน"
              className="!text-[#72E128] !bg-[#EEFBE5]"
            />
          ) : (
            <Chip label="ปิดใช้งาน" className="!text-[#6D788D] !bg-[#F4F4F4]" />
          )}
        </>
      ),
    },
  ]);

  return (
    <>
      <Card className="!rounded-lg mb-4">
        <div className="py-4">
          <div className="d-flex align-items-center px-4 mb-4">
            <button
              className="btn btn-export-st-1 me-auto"
              onClick={handleExport}
            >
              Export
            </button>
            <div className="me-3">
              <input
                type="text"
                className="form-control form-st-4"
                placeholder="ค้นหาจากชื่อกิจการ หรือ รหัสกิจการ"
                onChange={(e) => debouncedSearch(e.target.value)}
              />
            </div>
            <Button
              variant="contained"
              className="!border-[#409FFB] !rounded-lg text-[#409FFB] !bg-[#2DC76D]"
              onClick={handleAddBusiness}
            >
              เพิ่มธุรกิจ
            </Button>
          </div>

          <AllderTable
            data={businessData}
            loading={loading}
            columns={columns}
            totalRows={totalRows}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            handleSort={handleSort}
          />
        </div>
      </Card>

      <BusinessModal
        modal={modalBusiness}
        setModal={handleModalClose}
        onSuccess={() => {
          setModalBusiness((prev) => ({
            ...prev,
            open: false,
          }));
          refreshData();
        }}
        businessGroupId={selectedCustomer?.business_group_id}
      />
    </>
  );
}
